import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Space,
  Typography,
  Row,
  Col,
  Tabs,
  Tag,
  Popconfirm,
  Upload,
  Alert,
  Statistic,
  Progress,
  Tooltip,
  Badge,
  Radio,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import './index.less';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const BASE_URL = '/api/v1/card-maker';

// 稀有度颜色：自由文本，按哈希取色
const RARITY_COLOR_POOL = ['#8c8c8c', '#1677ff', '#722ed1', '#faad14', '#52c41a', '#ff4d4f', '#eb2f96', '#13c2c2'];
const getRarityColor = (v: string) => {
  if (!v) return '#8c8c8c';
  let hash = 0;
  for (let i = 0; i < v.length; i++) hash = v.charCodeAt(i) + ((hash << 5) - hash);
  return RARITY_COLOR_POOL[Math.abs(hash) % RARITY_COLOR_POOL.length];
};

interface CardSet {
  id: number;
  code: string;
  name: string;
  description?: string;
  thumbnailUrl?: string;
  created: string;
}

interface CardItem {
  id: number;
  cardSetId: number;
  cardNumber: string;
  name: string;
  thumbnailUrl?: string;
  rarity: string;
  description?: string;
}

interface CardSku {
  id: number;
  cardSetId: number;
  name: string;
  packSize: number;
  description?: string;
}

interface SkuRatio {
  id: number;
  skuId: number;
  cardId: number;
  quantity: number;
  card: CardItem;
}

interface SkuRule {
  id: number;
  skuId: number;
  rarity: string;
  operator: string; // 'exactly' | 'at_least' | 'at_most'
  count: number;
}

const OPERATOR_LABELS: Record<string, string> = {
  exactly: '恰好',
  at_least: '至少',
  at_most: '最多',
};

// ==================== Card Sets Tab ====================

const CardSetsTab: React.FC<{ onSetChange: () => void }> = ({ onSetChange }) => {
  const [sets, setSets] = useState<CardSet[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingSet, setEditingSet] = useState<CardSet | null>(null);
  const [form] = Form.useForm();
  const [setThumbUploading, setSetThumbUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/sets`);
      const json = await res.json();
      if (json.success) setSets(json.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditingSet(null); form.resetFields(); setModalVisible(true); };
  const openEdit = (record: CardSet) => { setEditingSet(record); form.setFieldsValue(record); setModalVisible(true); };

  const handleSubmit = async (values: any) => {
    try {
      const url = editingSet ? `${BASE_URL}/sets/${editingSet.id}` : `${BASE_URL}/sets`;
      const method = editingSet ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(values) });
      const json = await res.json();
      if (json.success) {
        message.success(editingSet ? '更新成功' : '创建成功');
        setModalVisible(false);
        load();
        onSetChange();
      } else {
        message.error(json.message || '操作失败');
      }
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const res = await fetch(`${BASE_URL}/sets/${id}`, { method: 'DELETE' });
      const json = await res.json();
      if (json.success) { message.success('删除成功'); load(); onSetChange(); }
      else message.error(json.message);
    } catch (e: any) { message.error(e.message); }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '编号', dataIndex: 'code', width: 100 },
    {
      title: '缩略图', dataIndex: 'thumbnailUrl', width: 100,
      render: (url: string) => url ? <img src={`${url}?x-oss-process=image/resize,w_200`} alt="" className="card-thumbnail" /> : '-',
    },
    { title: '名称', dataIndex: 'name' },
    { title: '描述', dataIndex: 'description', ellipsis: true },
    {
      title: '创建时间', dataIndex: 'created', width: 170,
      render: (v: string) => new Date(v).toLocaleString('zh-CN'),
    },
    {
      title: '操作', width: 120,
      render: (_: any, record: CardSet) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Popconfirm title="确认删除？" onConfirm={() => handleDelete(record.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="卡牌系列管理"
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load}>刷新</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>新增系列</Button>
        </Space>
      }
    >
      <Table rowKey="id" dataSource={sets} columns={columns} loading={loading} pagination={{ pageSize: 20 }} />
      <Modal
        title={editingSet ? '编辑系列' : '新增系列'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        okText="保存"
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="code" label="系列编号" rules={[{ required: true }]}>
            <Input placeholder="如: CM001" />
          </Form.Item>
          <Form.Item name="name" label="系列名称" rules={[{ required: true }]}>
            <Input placeholder="如: 深海巨兽系列" />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="thumbnailUrl" label="缩略图">
            <Input placeholder="https://..." style={{ marginBottom: 8 }} />
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.thumbnailUrl !== cur.thumbnailUrl || prev.code !== cur.code}>
              {({ getFieldValue, setFieldValue }) => {
                const url = getFieldValue('thumbnailUrl');
                return (
                  <Space align="start">
                    {url && <img src={`${url}?x-oss-process=image/resize,w_200`} alt="缩略图" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4, border: '1px solid #d9d9d9' }} />}
                    <Upload
                      accept="image/*"
                      showUploadList={false}
                      beforeUpload={async (file) => {
                        const code = getFieldValue('code');
                        if (!code) { message.warning('请先填写系列编号'); return false; }
                        setSetThumbUploading(true);
                        try {
                          const fd = new FormData();
                          fd.append('setCode', code);
                          fd.append('file', file);
                          const res = await fetch(`${BASE_URL}/sets/upload-thumbnail`, { method: 'POST', body: fd });
                          const json = await res.json();
                          if (json.success) {
                            setFieldValue('thumbnailUrl', json.data.url);
                            message.success('上传成功');
                          } else {
                            message.error(json.message);
                          }
                        } catch (e: any) {
                          message.error(e.message);
                        } finally {
                          setSetThumbUploading(false);
                        }
                        return false;
                      }}
                    >
                      <Button icon={<UploadOutlined />} loading={setThumbUploading} size="small">上传图片</Button>
                    </Upload>
                  </Space>
                );
              }}
            </Form.Item>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

// ==================== Cards Tab ====================

interface CardWithInventory extends CardItem {
  stock: number;
  totalConsumed: number;
  monthlyConsumption: number;
  monthsRemaining: number | null;
  isLowStock: boolean;
  isCriticalStock: boolean;
  restockSuggestion: number;
}

interface ImportRow {
  cardNumber: string;
  name: string;
  rarity: string;
  thumbnailUrl: string;
  description: string;
  status: 'pending' | 'success' | 'error';
  error?: string;
}

const CardsTab: React.FC<{ sets: CardSet[] }> = ({ sets }) => {
  const [selectedSetId, setSelectedSetId] = useState<number | null>(null);
  const [cards, setCards] = useState<CardWithInventory[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCard, setEditingCard] = useState<CardItem | null>(null);
  const [form] = Form.useForm();

  // 库存设置（单张）
  const [stockModalVisible, setStockModalVisible] = useState(false);
  const [stockCard, setStockCard] = useState<CardWithInventory | null>(null);
  const [stockMode, setStockMode] = useState<'set' | 'add'>('set');
  const [stockForm] = Form.useForm();

  // 批量库存设置
  const [batchModalVisible, setBatchModalVisible] = useState(false);
  const [batchMode, setBatchMode] = useState<'set' | 'add'>('add');
  const [batchValues, setBatchValues] = useState<Record<number, number | undefined>>({});
  const [batchNotes, setBatchNotes] = useState('');
  const [batchLoading, setBatchLoading] = useState(false);
  // 按稀有度快速填写：rarity -> 临时输入值
  const [rarityQuickValues, setRarityQuickValues] = useState<Record<string, number | undefined>>({});

  // Excel import state
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importRows, setImportRows] = useState<ImportRow[]>([]);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importDone, setImportDone] = useState(false);

  // 批量缩略图上传
  const [thumbModalVisible, setThumbModalVisible] = useState(false);
  const [thumbFiles, setThumbFiles] = useState<File[]>([]);
  const [thumbUploading, setThumbUploading] = useState(false);
  interface ThumbResult { filename: string; cardNumber: string; status: 'success' | 'not_found' | 'error'; url?: string; error?: string; }
  const [thumbResults, setThumbResults] = useState<ThumbResult[]>([]);

  const load = useCallback(async () => {
    if (!selectedSetId) return;
    setLoading(true);
    try {
      // 优先使用带库存统计的接口
      try {
        const res = await fetch(`${BASE_URL}/card-inventory?setId=${selectedSetId}`);
        const json = await res.json();
        if (json.success) {
          setCards(json.data);
          return;
        }
      } catch { /* 降级处理 */ }

      // 降级：仅加载卡牌基础数据（新表迁移前或接口异常时）
      try {
        const res = await fetch(`${BASE_URL}/cards?setId=${selectedSetId}`);
        const json = await res.json();
        if (json.success) {
          setCards(json.data.map((c: CardItem) => ({
            ...c,
            stock: 0, totalConsumed: 0,
            monthlyConsumption: 0, monthsRemaining: null,
            isLowStock: false, isCriticalStock: false, restockSuggestion: 0,
          })));
        } else {
          message.error(json.message || '加载失败');
        }
      } catch (e: any) {
        message.error(`加载卡牌失败：${e.message}`);
      }
    } finally {
      setLoading(false);
    }
  }, [selectedSetId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingCard(null);
    form.resetFields();
    form.setFieldsValue({ cardSetId: selectedSetId });
    setModalVisible(true);
  };
  const openEdit = (record: CardItem) => { setEditingCard(record); form.setFieldsValue(record); setModalVisible(true); };

  const handleSubmit = async (values: any) => {
    try {
      const url = editingCard ? `${BASE_URL}/cards/${editingCard.id}` : `${BASE_URL}/cards`;
      const method = editingCard ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(values) });
      const json = await res.json();
      if (json.success) { message.success('保存成功'); setModalVisible(false); load(); }
      else message.error(json.message);
    } catch (e: any) { message.error(e.message); }
  };

  const handleDelete = async (id: number) => {
    try {
      const res = await fetch(`${BASE_URL}/cards/${id}`, { method: 'DELETE' });
      const json = await res.json();
      if (json.success) { message.success('删除成功'); load(); }
      else message.error(json.message);
    } catch (e: any) { message.error(e.message); }
  };

  // ---- 单张库存设置 ----
  const openStockModal = (card: CardWithInventory) => {
    setStockCard(card);
    setStockMode('set');
    stockForm.setFieldsValue({ stock: card.stock, delta: 0, notes: '' });
    setStockModalVisible(true);
  };

  const handleStockSubmit = async (values: { stock: number; delta: number; notes: string }) => {
    if (!stockCard) return;
    const newStock = stockMode === 'add'
      ? Math.max(0, stockCard.stock + (values.delta || 0))
      : values.stock;
    try {
      const res = await fetch(`${BASE_URL}/card-inventory`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cardId: stockCard.id, stock: newStock, notes: values.notes }),
      });
      const json = await res.json();
      if (json.success) { message.success('库存更新成功'); setStockModalVisible(false); load(); }
      else message.error(json.message);
    } catch (e: any) { message.error(e.message); }
  };

  // ---- 批量库存设置 ----
  const openBatchModal = () => {
    setBatchMode('add');
    setBatchValues({});
    setBatchNotes('');
    setRarityQuickValues({});
    setBatchModalVisible(true);
  };

  const applyRarityQuickFill = (rarity: string) => {
    const val = rarityQuickValues[rarity];
    if (val === undefined || val === null) return;
    const updates: Record<number, number> = {};
    cards.filter(c => c.rarity === rarity).forEach(c => { updates[c.id] = val; });
    setBatchValues(prev => ({ ...prev, ...updates }));
  };

  const handleBatchSave = async () => {
    const entries = Object.entries(batchValues).filter(([, v]) => v !== undefined && v !== null) as [string, number][];
    if (entries.length === 0) { message.warning('未修改任何库存值'); return; }
    setBatchLoading(true);
    let successCount = 0;
    for (const [cardIdStr, val] of entries) {
      const cardId = parseInt(cardIdStr);
      const card = cards.find(c => c.id === cardId);
      if (!card) continue;
      const newStock = batchMode === 'add' ? Math.max(0, card.stock + val) : Math.max(0, val);
      try {
        const res = await fetch(`${BASE_URL}/card-inventory`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cardId, stock: newStock, notes: batchNotes || undefined }),
        });
        const json = await res.json();
        if (json.success) successCount++;
      } catch {}
    }
    setBatchLoading(false);
    message.success(`批量更新完成：${successCount}/${entries.length} 成功`);
    setBatchModalVisible(false);
    load();
  };

  // ---- Excel 模板下载 ----
  const downloadTemplate = () => {
    const templateData = [
      ['卡牌编号', '卡牌名称', '稀有度', '缩略图URL', '描述'],
      ['001', '示例卡A', 'N', 'https://example.com/a.jpg', '这是一张普通卡'],
      ['002', '示例卡B', 'R', 'https://example.com/b.jpg', '这是一张稀有卡'],
      ['003', '示例卡C', 'SR', 'https://example.com/c.jpg', '这是一张超稀有卡'],
    ];
    const ws = XLSX.utils.aoa_to_sheet(templateData);
    // 设置列宽
    ws['!cols'] = [{ wch: 12 }, { wch: 20 }, { wch: 12 }, { wch: 40 }, { wch: 30 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '卡牌模板');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), '卡牌批量导入模板.xlsx');
  };

  // ---- Excel 文件解析 ----
  const handleFileSelect = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1 }) as any[][];

        if (rows.length < 2) { message.error('文件内容为空'); return; }

        const headers: string[] = rows[0].map((h: any) => String(h).trim());
        const colIdx = (name: string) => headers.indexOf(name);

        const numIdx    = colIdx('卡牌编号');
        const nameIdx   = colIdx('卡牌名称');
        const rarityIdx = colIdx('稀有度');
        const thumbIdx  = colIdx('缩略图URL');
        const descIdx   = colIdx('描述');

        if (numIdx === -1 || nameIdx === -1) {
          message.error('文件格式错误，必须包含「卡牌编号」和「卡牌名称」列');
          return;
        }

        const parsed: ImportRow[] = [];
        for (let i = 1; i < rows.length; i++) {
          const row = rows[i];
          const num  = String(row[numIdx]  ?? '').trim();
          const name = String(row[nameIdx] ?? '').trim();
          if (!num && !name) continue; // 跳过空行
          parsed.push({
            cardNumber:   num,
            name:         name,
            rarity:       rarityIdx !== -1 ? String(row[rarityIdx] ?? '').trim() : '',
            thumbnailUrl: thumbIdx  !== -1 ? String(row[thumbIdx]  ?? '').trim() : '',
            description:  descIdx   !== -1 ? String(row[descIdx]   ?? '').trim() : '',
            status: 'pending',
          });
        }

        if (parsed.length === 0) { message.error('未解析到有效数据'); return; }

        // 检测文件内重复编号
        const seenInFile = new Map<string, number>(); // cardNumber -> first row index
        for (let i = 0; i < parsed.length; i++) {
          const num = parsed[i].cardNumber;
          if (!num) {
            parsed[i] = { ...parsed[i], status: 'error', error: '卡牌编号不能为空' };
            continue;
          }
          if (seenInFile.has(num)) {
            const firstIdx = seenInFile.get(num)!;
            if (parsed[firstIdx].status !== 'error') {
              parsed[firstIdx] = { ...parsed[firstIdx], status: 'error', error: '文件内编号重复' };
            }
            parsed[i] = { ...parsed[i], status: 'error', error: `编号重复（与第 ${firstIdx + 2} 行相同）` };
          } else {
            seenInFile.set(num, i);
          }
        }

        // 检测与现有卡牌的编号冲突
        const existingNumbers = new Set(cards.map(c => c.cardNumber));
        for (let i = 0; i < parsed.length; i++) {
          if (parsed[i].status === 'error') continue;
          if (existingNumbers.has(parsed[i].cardNumber)) {
            parsed[i] = { ...parsed[i], status: 'error', error: '编号已存在于当前系列' };
          }
        }

        const errorCount = parsed.filter(r => r.status === 'error').length;
        setImportRows(parsed);
        setImportDone(false);
        setImportProgress(0);
        setImportModalVisible(true);
        if (errorCount > 0) {
          message.warning(`解析完成：共 ${parsed.length} 条，其中 ${errorCount} 条编号冲突已标记`);
        } else {
          message.success(`解析成功，共 ${parsed.length} 条`);
        }
      } catch {
        message.error('文件解析失败，请检查格式');
      }
    };
    reader.readAsArrayBuffer(file);
    return false; // 阻止默认上传
  };

  // ---- 批量导入执行 ----
  const handleImport = async () => {
    if (!selectedSetId) return;
    setImporting(true);
    setImportProgress(0);
    const rows = [...importRows];
    const preSkipped = rows.filter(r => r.status === 'error').length;
    const toProcess = rows.filter(r => r.status === 'pending').length;
    let processed = 0;

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (row.status === 'error') continue; // 跳过重复/校验失败行
      try {
        const res = await fetch(`${BASE_URL}/cards`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            cardSetId:    selectedSetId,
            cardNumber:   row.cardNumber,
            name:         row.name,
            rarity:       row.rarity || 'N',
            thumbnailUrl: row.thumbnailUrl || undefined,
            description:  row.description || undefined,
          }),
        });
        const json = await res.json();
        rows[i] = { ...row, status: json.success ? 'success' : 'error', error: json.success ? undefined : json.message };
      } catch (e: any) {
        rows[i] = { ...row, status: 'error', error: e.message };
      }
      processed++;
      setImportProgress(toProcess > 0 ? Math.round((processed / toProcess) * 100) : 100);
      setImportRows([...rows]);
    }

    setImporting(false);
    setImportDone(true);
    const successCount = rows.filter(r => r.status === 'success').length;
    const apiErrors = rows.filter(r => r.status === 'error').length - preSkipped;
    let msg = `导入完成：成功 ${successCount} 条`;
    if (preSkipped > 0) msg += `，跳过重复 ${preSkipped} 条`;
    if (apiErrors > 0) msg += `，失败 ${apiErrors} 条`;
    message.success(msg);
    load();
  };

  const handleBatchThumbUpload = async () => {
    if (!selectedSetId || thumbFiles.length === 0) return;
    setThumbUploading(true);
    setThumbResults([]);
    try {
      const formData = new FormData();
      formData.append('setId', String(selectedSetId));
      thumbFiles.forEach(f => formData.append('files', f));
      const res = await fetch(`${BASE_URL}/cards/batch-upload-thumbnails`, { method: 'POST', body: formData });
      const json = await res.json();
      if (json.success) {
        setThumbResults(json.data);
        const successCount = json.data.filter((r: ThumbResult) => r.status === 'success').length;
        message.success(`上传完成：${successCount}/${json.data.length} 成功`);
        load();
      } else {
        message.error(json.message);
      }
    } catch (e: any) {
      message.error(e.message);
    } finally {
      setThumbUploading(false);
    }
  };

  const importColumns = [
    { title: '编号', dataIndex: 'cardNumber', width: 80 },
    { title: '名称', dataIndex: 'name' },
    { title: '稀有度', dataIndex: 'rarity', width: 80 },
    { title: '缩略图URL', dataIndex: 'thumbnailUrl', ellipsis: true },
    { title: '描述', dataIndex: 'description', ellipsis: true },
    {
      title: '状态', dataIndex: 'status', width: 90,
      render: (v: string, row: ImportRow) => {
        if (v === 'success') return <Tag color="green">成功</Tag>;
        if (v === 'error')   return <Tooltip title={row.error}><Tag color="red">失败</Tag></Tooltip>;
        return <Tag>待导入</Tag>;
      },
    },
  ];

  const columns = [
    { title: '编号', dataIndex: 'cardNumber', width: 70 },
    {
      title: '缩略图', dataIndex: 'thumbnailUrl', width: 70,
      render: (url: string) => url ? <img src={`${url}?x-oss-process=image/resize,w_200`} alt="" className="card-thumbnail" /> : '-',
    },
    { title: '名称', dataIndex: 'name', width: 130 },
    {
      title: '稀有度', dataIndex: 'rarity', width: 80,
      render: (v: string) => v ? <Tag color={getRarityColor(v)}>{v}</Tag> : '-',
    },
    {
      title: '库存(张)', dataIndex: 'stock', width: 90,
      render: (v: number, r: CardWithInventory) => (
        <span
          className={r.isCriticalStock ? 'stock-critical' : r.isLowStock ? 'stock-warning' : 'stock-ok'}
          style={{ fontWeight: 600, cursor: 'pointer' }}
          onClick={() => openStockModal(r)}
        >
          {v}
        </span>
      ),
    },
    {
      title: '月消耗', dataIndex: 'monthlyConsumption', width: 80,
      render: (v: number) => v > 0 ? v : <Text type="secondary">-</Text>,
    },
    {
      title: '预计余量', dataIndex: 'monthsRemaining', width: 90,
      render: (v: number | null, r: CardWithInventory) => {
        if (v === null) return <Text type="secondary">-</Text>;
        return (
          <span className={r.isCriticalStock ? 'stock-critical' : r.isLowStock ? 'stock-warning' : 'stock-ok'}>
            {v.toFixed(1)} 月
          </span>
        );
      },
    },
    {
      title: '状态', width: 90,
      render: (_: any, r: CardWithInventory) => {
        if (r.monthlyConsumption === 0) return <Tag>无消耗</Tag>;
        if (r.isCriticalStock) return <Tag color="red">严重不足</Tag>;
        if (r.isLowStock) return <Tag color="orange">需补货</Tag>;
        return <Tag color="green">充足</Tag>;
      },
    },
    {
      title: '建议补货', dataIndex: 'restockSuggestion', width: 90,
      render: (v: number) => v > 0 ? <Text type="danger" strong>{v} 张</Text> : '-',
    },
    {
      title: '操作', width: 130,
      render: (_: any, record: CardWithInventory) => (
        <Space>
          <Button size="small" onClick={() => openStockModal(record)}>库存</Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Popconfirm title="确认删除？" onConfirm={() => handleDelete(record.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const successCount = importRows.filter(r => r.status === 'success').length;
  const errorCount   = importRows.filter(r => r.status === 'error').length;

  return (
    <Card
      title="卡牌管理"
      extra={
        <Space>
          <Select
            placeholder="选择系列"
            style={{ width: 200 }}
            value={selectedSetId}
            onChange={setSelectedSetId}
            options={sets.map(s => ({ value: s.id, label: `${s.code} ${s.name}` }))}
          />
          <Button icon={<ReloadOutlined />} onClick={load} disabled={!selectedSetId}>刷新</Button>
          <Button onClick={openBatchModal} disabled={!selectedSetId || cards.length === 0}>批量入库</Button>
          <Button icon={<UploadOutlined />} onClick={() => { setThumbModalVisible(true); setThumbFiles([]); setThumbResults([]); }} disabled={!selectedSetId || cards.length === 0}>批量上传缩略图</Button>
          <Button icon={<DownloadOutlined />} onClick={downloadTemplate} disabled={!selectedSetId}>下载模板</Button>
          <Upload accept=".xlsx,.xls" showUploadList={false} beforeUpload={handleFileSelect} disabled={!selectedSetId}>
            <Button icon={<FileExcelOutlined />} disabled={!selectedSetId}>导入Excel</Button>
          </Upload>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} disabled={!selectedSetId}>新增卡牌</Button>
        </Space>
      }
    >
      {!selectedSetId && <Alert message="请先选择卡牌系列" type="info" showIcon />}
      {selectedSetId && (() => {
        const critical = cards.filter(c => c.isCriticalStock);
        const low = cards.filter(c => c.isLowStock && !c.isCriticalStock);
        return (
          <>
            {critical.length > 0 && (
              <Alert type="error" showIcon icon={<WarningOutlined />} style={{ marginBottom: 12 }}
                message={`${critical.length} 张卡库存严重不足（不足0.5个月用量）：${critical.map(c => `${c.cardNumber} ${c.name}`).join('、')}`}
              />
            )}
            {low.length > 0 && (
              <Alert type="warning" showIcon style={{ marginBottom: 12 }}
                message={`${low.length} 张卡库存不足1个月：${low.map(c => `${c.cardNumber}(建议补${c.restockSuggestion}张)`).join('、')}`}
              />
            )}
            <Table rowKey="id" dataSource={cards} columns={columns} loading={loading}
              pagination={{ pageSize: 50 }}
              rowClassName={(r: CardWithInventory) => r.isCriticalStock ? 'ant-table-row-danger' : r.isLowStock ? 'ant-table-row-warn' : ''}
            />
          </>
        );
      })()}

      {/* 单条新增/编辑 Modal */}
      <Modal
        title={editingCard ? '编辑卡牌' : '新增卡牌'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        okText="保存"
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="cardSetId" hidden><Input /></Form.Item>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="cardNumber" label="卡牌编号" rules={[{ required: true }]}>
                <Input placeholder="001" />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item name="name" label="卡牌名称" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="rarity" label="稀有度" extra="可自由填写，如：N、R、SR、SSR、UR 等">
            <Input placeholder="如: N / R / SR / SSR / UR" />
          </Form.Item>
          <Form.Item name="thumbnailUrl" label="缩略图URL">
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 库存设置 Modal（单张） */}
      <Modal
        title={stockCard ? `库存操作 — ${stockCard.cardNumber} ${stockCard.name}` : '库存操作'}
        open={stockModalVisible}
        onCancel={() => setStockModalVisible(false)}
        onOk={() => stockForm.submit()}
        okText="保存"
      >
        {stockCard && (
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={8}><Statistic title="当前库存" value={stockCard.stock} suffix="张" /></Col>
            <Col span={8}><Statistic title="月均消耗" value={stockCard.monthlyConsumption || '-'} suffix={stockCard.monthlyConsumption ? '张' : ''} /></Col>
            <Col span={8}><Statistic title="累计消耗" value={stockCard.totalConsumed} suffix="张" /></Col>
          </Row>
        )}
        <Radio.Group
          value={stockMode}
          onChange={e => { setStockMode(e.target.value); stockForm.resetFields(['stock', 'delta']); }}
          style={{ marginBottom: 16 }}
          optionType="button"
          buttonStyle="solid"
        >
          <Radio.Button value="set">直接设置</Radio.Button>
          <Radio.Button value="add">追加入库</Radio.Button>
        </Radio.Group>
        <Form form={stockForm} layout="vertical" onFinish={handleStockSubmit}>
          {stockMode === 'set' ? (
            <Form.Item name="stock" label="设置库存数量（张）" rules={[{ required: true }]}
              extra="直接填写当前实际库存张数，系统将以此为准">
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          ) : (
            <Form.Item
              name="delta"
              label="追加数量（张）"
              rules={[{ required: true }]}
              extra={stockCard
                ? `当前 ${stockCard.stock} + 追加 = 新库存`
                : '填写本次入库张数，将累加到当前库存'}
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          )}
          <Form.Item name="notes" label="备注（入库原因等）">
            <Input.TextArea rows={2} placeholder="如：2026-02 入库 500 张" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 批量库存设置 Modal */}
      {(() => {
        const uniqueRarities = Array.from(new Set(cards.map(c => c.rarity).filter(Boolean)));
        const changedCount = Object.values(batchValues).filter(v => v !== undefined).length;
        const batchColumns = [
          { title: '编号', dataIndex: 'cardNumber', width: 70 },
          { title: '名称', dataIndex: 'name' },
          {
            title: '稀有度', dataIndex: 'rarity', width: 80,
            render: (v: string) => v ? <Tag color={getRarityColor(v)}>{v}</Tag> : '-',
          },
          {
            title: '当前库存', dataIndex: 'stock', width: 90,
            render: (v: number) => <Text type="secondary">{v} 张</Text>,
          },
          {
            title: batchMode === 'add' ? '追加数量' : '设置为',
            width: 140,
            render: (_: any, r: CardWithInventory) => (
              <InputNumber
                min={0}
                size="small"
                style={{ width: '100%' }}
                value={batchValues[r.id]}
                placeholder="不修改"
                onChange={val => setBatchValues(prev => ({ ...prev, [r.id]: val ?? undefined }))}
              />
            ),
          },
          {
            title: '操作后库存', width: 100,
            render: (_: any, r: CardWithInventory) => {
              const v = batchValues[r.id];
              if (v === undefined) return <Text type="secondary">不变</Text>;
              const result = batchMode === 'add' ? r.stock + v : v;
              return <Text strong style={{ color: result < r.stock ? '#ff4d4f' : '#52c41a' }}>{result} 张</Text>;
            },
          },
        ];
        return (
          <Modal
            title="批量库存操作"
            open={batchModalVisible}
            onCancel={() => setBatchModalVisible(false)}
            width={760}
            footer={
              <Space>
                <Button onClick={() => setBatchModalVisible(false)}>取消</Button>
                <Button
                  type="primary"
                  loading={batchLoading}
                  disabled={changedCount === 0}
                  onClick={handleBatchSave}
                >
                  保存 {changedCount > 0 ? `（${changedCount} 张）` : ''}
                </Button>
              </Space>
            }
          >
            <Row gutter={12} align="middle" style={{ marginBottom: 16 }}>
              <Col>
                <Text strong>操作方式：</Text>
              </Col>
              <Col>
                <Radio.Group
                  value={batchMode}
                  onChange={e => { setBatchMode(e.target.value); setBatchValues({}); }}
                  optionType="button"
                  buttonStyle="solid"
                >
                  <Radio.Button value="add">追加入库</Radio.Button>
                  <Radio.Button value="set">直接设置</Radio.Button>
                </Radio.Group>
              </Col>
            </Row>

            {uniqueRarities.length > 0 && (
              <>
                <Divider orientation="left" plain style={{ margin: '0 0 12px' }}>按稀有度快速填写</Divider>
                <Row gutter={[12, 8]} style={{ marginBottom: 16 }}>
                  {uniqueRarities.map(rarity => (
                    <Col key={rarity}>
                      <Space size={4}>
                        <Tag color={getRarityColor(rarity)} style={{ margin: 0, minWidth: 36, textAlign: 'center' }}>{rarity}</Tag>
                        <InputNumber
                          min={0}
                          size="small"
                          style={{ width: 90 }}
                          placeholder="数量"
                          value={rarityQuickValues[rarity]}
                          onChange={val => setRarityQuickValues(prev => ({ ...prev, [rarity]: val ?? undefined }))}
                        />
                        <Button
                          size="small"
                          type="dashed"
                          onClick={() => applyRarityQuickFill(rarity)}
                          disabled={rarityQuickValues[rarity] === undefined}
                        >
                          填入全部 {rarity}
                        </Button>
                      </Space>
                    </Col>
                  ))}
                </Row>
              </>
            )}

            <Divider orientation="left" plain style={{ margin: '0 0 12px' }}>逐张调整</Divider>
            <Table
              rowKey="id"
              dataSource={cards}
              columns={batchColumns}
              size="small"
              pagination={{ pageSize: 20 }}
              scroll={{ y: 320 }}
            />

            <div style={{ marginTop: 12 }}>
              <Text type="secondary">备注（可选）：</Text>
              <Input.TextArea
                rows={2}
                style={{ marginTop: 4 }}
                placeholder="如：2026-02 批量入库"
                value={batchNotes}
                onChange={e => setBatchNotes(e.target.value)}
              />
            </div>
          </Modal>
        );
      })()}

      {/* Excel 批量导入 Modal */}
      <Modal
        title="Excel 批量导入卡牌"
        open={importModalVisible}
        onCancel={() => { if (!importing) { setImportModalVisible(false); } }}
        width={800}
        footer={
          importDone ? (
            <Button type="primary" onClick={() => setImportModalVisible(false)}>关闭</Button>
          ) : (
            <Space>
              <Button onClick={() => setImportModalVisible(false)} disabled={importing}>取消</Button>
              <Button type="primary" onClick={handleImport} loading={importing} icon={<FileExcelOutlined />}>
                确认导入 {importRows.filter(r => r.status === 'pending').length} 条
              </Button>
            </Space>
          )
        }
      >
        {importing && (
          <Progress percent={importProgress} style={{ marginBottom: 16 }} />
        )}
        {importDone && (
          <Alert
            type={errorCount > 0 ? 'warning' : 'success'}
            message={`导入完成：成功 ${successCount} 条，失败 ${errorCount} 条`}
            style={{ marginBottom: 16 }}
            showIcon
          />
        )}
        <Table
          rowKey={(r, i) => String(i)}
          dataSource={importRows}
          columns={importColumns}
          size="small"
          pagination={{ pageSize: 20 }}
          scroll={{ y: 400 }}
        />
      </Modal>

      {/* 批量缩略图上传 Modal */}
      <Modal
        title="批量上传卡牌缩略图"
        open={thumbModalVisible}
        onCancel={() => { if (!thumbUploading) setThumbModalVisible(false); }}
        width={680}
        footer={
          <Space>
            <Button onClick={() => setThumbModalVisible(false)} disabled={thumbUploading}>关闭</Button>
            <Button
              type="primary"
              icon={<UploadOutlined />}
              loading={thumbUploading}
              disabled={thumbFiles.length === 0}
              onClick={handleBatchThumbUpload}
            >
              开始上传 {thumbFiles.length > 0 ? `（${thumbFiles.length} 张）` : ''}
            </Button>
          </Space>
        }
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="图片文件名即为卡牌编号（如 001.jpg → 编号 001），上传后自动匹配更新对应卡牌的缩略图 URL。"
        />
        <Upload.Dragger
          multiple
          accept="image/*"
          showUploadList={{ showRemoveIcon: true }}
          fileList={thumbFiles.map((f, i) => ({ uid: String(i), name: f.name, status: 'done' as const, size: f.size, type: f.type }))}
          beforeUpload={(_, fileList) => {
            setThumbFiles(prev => {
              const existing = new Set(prev.map(f => f.name));
              const newFiles = fileList.filter(f => !existing.has(f.name));
              return [...prev, ...newFiles];
            });
            return false;
          }}
          onRemove={file => {
            setThumbFiles(prev => prev.filter(f => f.name !== file.name));
          }}
          disabled={thumbUploading}
        >
          <p className="ant-upload-drag-icon"><UploadOutlined /></p>
          <p className="ant-upload-text">拖拽或点击选择图片（可多选）</p>
          <p className="ant-upload-hint">支持 JPG、PNG、WebP 等格式，文件名需与卡牌编号对应</p>
        </Upload.Dragger>

        {thumbResults.length > 0 && (
          <Table
            style={{ marginTop: 16 }}
            rowKey="filename"
            dataSource={thumbResults}
            size="small"
            pagination={false}
            scroll={{ y: 240 }}
            columns={[
              { title: '文件名', dataIndex: 'filename', ellipsis: true },
              { title: '编号', dataIndex: 'cardNumber', width: 80 },
              {
                title: '结果', dataIndex: 'status', width: 100,
                render: (s: string, r: ThumbResult) => {
                  if (s === 'success') return <Tag color="green">成功</Tag>;
                  if (s === 'not_found') return <Tag color="orange">未匹配</Tag>;
                  return <Tooltip title={r.error}><Tag color="red">失败</Tag></Tooltip>;
                },
              },
            ]}
          />
        )}
      </Modal>
    </Card>
  );
};

// ==================== SKU Tab ====================

const SkuTab: React.FC<{ sets: CardSet[] }> = ({ sets }) => {
  const [selectedSetId, setSelectedSetId] = useState<number | null>(null);
  const [skus, setSkus] = useState<CardSku[]>([]);
  const [cards, setCards] = useState<CardItem[]>([]);
  const [ratios, setRatios] = useState<SkuRatio[]>([]);
  const [loading, setLoading] = useState(false);
  const [skuModalVisible, setSkuModalVisible] = useState(false);
  const [editingSku, setEditingSku] = useState<CardSku | null>(null);
  const [selectedSku, setSelectedSku] = useState<CardSku | null>(null);
  const [skuForm] = Form.useForm();
  const [ratioQuickValues, setRatioQuickValues] = useState<Record<string, number | undefined>>({});
  const [ratioQuickLoading, setRatioQuickLoading] = useState(false);
  const [localQuantities, setLocalQuantities] = useState<Record<number, number>>({});
  const [rules, setRules] = useState<SkuRule[]>([]);
  const [ruleForm] = Form.useForm();
  const [skuThumbUploading, setSkuThumbUploading] = useState(false);

  const loadSkus = useCallback(async () => {
    if (!selectedSetId) return;
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/skus?setId=${selectedSetId}`);
      const json = await res.json();
      if (json.success) setSkus(json.data);
    } finally { setLoading(false); }
  }, [selectedSetId]);

  const loadCards = useCallback(async () => {
    if (!selectedSetId) return;
    const res = await fetch(`${BASE_URL}/cards?setId=${selectedSetId}`);
    const json = await res.json();
    if (json.success) setCards(json.data);
  }, [selectedSetId]);

  const loadRatios = useCallback(async (skuId: number) => {
    const res = await fetch(`${BASE_URL}/ratios?skuId=${skuId}`);
    const json = await res.json();
    if (json.success) setRatios(json.data);
  }, []);

  useEffect(() => { loadSkus(); loadCards(); }, [loadSkus, loadCards]);

  // 每次 ratios 刷新时同步本地数量
  useEffect(() => {
    const map: Record<number, number> = {};
    ratios.forEach(r => { map[r.cardId] = r.quantity; });
    setLocalQuantities(map);
  }, [ratios]);

  const handleSkuSubmit = async (values: any) => {
    try {
      const url = editingSku ? `${BASE_URL}/skus/${editingSku.id}` : `${BASE_URL}/skus`;
      const method = editingSku ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...values, cardSetId: selectedSetId }) });
      const json = await res.json();
      if (json.success) { message.success('保存成功'); setSkuModalVisible(false); loadSkus(); }
      else message.error(json.message);
    } catch (e: any) { message.error(e.message); }
  };

  const handleQuantityChange = async (cardId: number, value: number) => {
    if (!selectedSku) return;
    const ratio = ratios.find(r => r.cardId === cardId);
    try {
      if (value > 0) {
        await fetch(`${BASE_URL}/ratios`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ skuId: selectedSku.id, cardId, quantity: value }),
        });
      } else if (ratio) {
        await fetch(`${BASE_URL}/ratios/${ratio.id}`, { method: 'DELETE' });
      }
    } catch {}
    loadRatios(selectedSku.id);
  };

  const handleDeleteSku = async (id: number) => {
    try {
      const res = await fetch(`${BASE_URL}/skus/${id}`, { method: 'DELETE' });
      const json = await res.json();
      if (json.success) { message.success('删除成功'); loadSkus(); if (selectedSku?.id === id) { setSelectedSku(null); setRatios([]); } }
      else message.error(json.message);
    } catch (e: any) { message.error(e.message); }
  };

  const loadRules = useCallback(async (skuId: number) => {
    const res = await fetch(`${BASE_URL}/sku-rules?skuId=${skuId}`);
    const json = await res.json();
    if (json.success) setRules(json.data);
  }, []);

  const handleUpsertRule = async (values: any) => {
    if (!selectedSku) return;
    const res = await fetch(`${BASE_URL}/sku-rules`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ skuId: selectedSku.id, ...values }),
    });
    const json = await res.json();
    if (json.success) {
      ruleForm.resetFields();
      loadRules(selectedSku.id);
    } else {
      message.error(json.message);
    }
  };

  const handleDeleteRule = async (id: number) => {
    await fetch(`${BASE_URL}/sku-rules/${id}`, { method: 'DELETE' });
    if (selectedSku) loadRules(selectedSku.id);
  };

  const handleRarityRatioFill = async (rarity: string) => {
    if (!selectedSku) return;
    const qty = ratioQuickValues[rarity];
    if (qty === undefined || qty === null || qty <= 0) return;
    const targets = cards.filter(c => c.rarity === rarity);
    if (targets.length === 0) return;
    setRatioQuickLoading(true);
    let count = 0;
    for (const card of targets) {
      try {
        const res = await fetch(`${BASE_URL}/ratios`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ skuId: selectedSku.id, cardId: card.id, quantity: qty }),
        });
        const json = await res.json();
        if (json.success) count++;
      } catch {}
    }
    setRatioQuickLoading(false);
    message.success(`已为 ${count} 张「${rarity}」卡设置数量 ${qty}`);
    loadRatios(selectedSku.id);
  };

  const totalCardsInBatch = ratios.reduce((sum, r) => sum + r.quantity, 0);
  const maxPacksFull = selectedSku && selectedSku.packSize > 0 ? Math.floor(totalCardsInBatch / selectedSku.packSize) : 0;
  const maxPacksRemainder = selectedSku && selectedSku.packSize > 0 ? totalCardsInBatch % selectedSku.packSize : 0;

  const skuColumns = [
    { title: '编码', dataIndex: 'code', width: 90, render: (v: string) => v || '-' },
    {
      title: '缩略图', dataIndex: 'thumbnailUrl', width: 70,
      render: (url: string) => url ? <img src={`${url}?x-oss-process=image/resize,w_200`} alt="" className="card-thumbnail" /> : '-',
    },
    { title: '名称', dataIndex: 'name' },
    { title: '每包张数', dataIndex: 'packSize', width: 100 },
    { title: '描述', dataIndex: 'description', ellipsis: true },
    {
      title: '操作', width: 160,
      render: (_: any, record: CardSku) => (
        <Space>
          <Button size="small" onClick={() => { setSelectedSku(record); setRatioQuickValues({}); setLocalQuantities({}); setRules([]); loadRatios(record.id); loadRules(record.id); }}>配置比例</Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => { setEditingSku(record); skuForm.setFieldsValue(record); setSkuModalVisible(true); }} />
          <Popconfirm title="确认删除？" onConfirm={() => handleDeleteSku(record.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const ratioColumns = [
    { title: '编号', dataIndex: 'cardNumber', width: 80 },
    { title: '名称', dataIndex: 'name' },
    {
      title: '稀有度', dataIndex: 'rarity', width: 80,
      render: (v: string) => v ? <Tag color={getRarityColor(v)}>{v}</Tag> : '-',
    },
    {
      title: '数量', dataIndex: 'id', width: 110,
      render: (cardId: number) => (
        <InputNumber
          min={0}
          size="small"
          style={{ width: 80 }}
          value={localQuantities[cardId] ?? 0}
          onChange={val => setLocalQuantities(prev => ({ ...prev, [cardId]: val ?? 0 }))}
          onBlur={() => handleQuantityChange(cardId, localQuantities[cardId] ?? 0)}
          onPressEnter={() => handleQuantityChange(cardId, localQuantities[cardId] ?? 0)}
        />
      ),
    },
    {
      title: '状态', dataIndex: 'id', width: 80,
      render: (cardId: number) => (localQuantities[cardId] ?? 0) > 0
        ? <Tag color="blue">已包含</Tag>
        : <Tag>未包含</Tag>,
    },
  ];

  return (
    <Card title="SKU管理">
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col>
          <Select
            placeholder="选择系列"
            style={{ width: 200 }}
            value={selectedSetId}
            onChange={v => { setSelectedSetId(v); setSelectedSku(null); setRatios([]); }}
            options={sets.map(s => ({ value: s.id, label: `${s.code} ${s.name}` }))}
          />
        </Col>
      </Row>

      {!selectedSetId && <Alert message="请先选择卡牌系列" type="info" showIcon />}

      {selectedSetId && (
        <Row gutter={16}>
          <Col span={10}>
            <Card
              size="small"
              title="SKU列表"
              extra={
                <Space>
                  <Button icon={<ReloadOutlined />} size="small" onClick={loadSkus} />
                  <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => { setEditingSku(null); skuForm.resetFields(); setSkuModalVisible(true); }}>新增SKU</Button>
                </Space>
              }
            >
              <Table rowKey="id" dataSource={skus} columns={skuColumns} loading={loading} pagination={false} size="small"
                rowClassName={r => r.id === selectedSku?.id ? 'ant-table-row-selected' : ''}
              />
            </Card>
          </Col>
          <Col span={14}>
            {selectedSku ? (
              <Card
                size="small"
                title={`「${selectedSku.name}」卡牌比例配置`}
              >
                <Row gutter={16} style={{ marginBottom: 12 }}>
                  <Col><Statistic title="每包张数" value={selectedSku.packSize} /></Col>
                  <Col><Statistic title="批次总卡数" value={totalCardsInBatch} /></Col>
                  <Col>
                    <Statistic
                      title="可生产包数"
                      value={maxPacksFull}
                      suffix={maxPacksRemainder > 0 ? `包 +${maxPacksRemainder}张` : '包'}
                    />
                  </Col>
                </Row>
                {(() => {
                  // ---- 规则冲突检测 ----
                  const totalAllLocal = cards.reduce((sum, c) => sum + (localQuantities[c.id] ?? 0), 0);
                  const totalPacksLocal = selectedSku && selectedSku.packSize > 0
                    ? Math.floor(totalAllLocal / selectedSku.packSize) : 0;

                  const ruleConflicts = rules.map(rule => {
                    const rarityTotal = cards
                      .filter(c => c.rarity === rule.rarity)
                      .reduce((sum, c) => sum + (localQuantities[c.id] ?? 0), 0);
                    const avgPerPack = totalPacksLocal > 0 ? rarityTotal / totalPacksLocal : 0;
                    let conflict = false;
                    if (totalPacksLocal > 0) {
                      if (rule.operator === 'exactly') conflict = rarityTotal !== rule.count * totalPacksLocal;
                      else if (rule.operator === 'at_least') conflict = rarityTotal < rule.count * totalPacksLocal;
                      else if (rule.operator === 'at_most') conflict = rarityTotal > rule.count * totalPacksLocal;
                    }
                    return { ...rule, avgPerPack, conflict };
                  });
                  const hasConflict = ruleConflicts.some(r => r.conflict);
                  const uniqueRarities = Array.from(new Set(cards.map(c => c.rarity).filter(Boolean)));

                  return (
                    <>
                      <Divider orientation="left" plain style={{ margin: '0 0 8px' }}>包装规则</Divider>
                      {hasConflict && (
                        <Alert
                          type="warning"
                          showIcon
                          style={{ marginBottom: 8 }}
                          message="当前配比与规则存在冲突，请调整数量"
                        />
                      )}
                      {ruleConflicts.length > 0 && (
                        <div style={{ marginBottom: 8 }}>
                          {ruleConflicts.map(rule => (
                            <Row key={rule.id} align="middle" style={{ marginBottom: 4 }} gutter={8}>
                              <Col flex="auto">
                                <Space size={4}>
                                  {rule.conflict
                                    ? <Text type="warning">⚠</Text>
                                    : <Text type="success">✓</Text>}
                                  <Text>每包{OPERATOR_LABELS[rule.operator]}</Text>
                                  <Text strong>{rule.count}</Text>
                                  <Text>张</Text>
                                  <Tag color={getRarityColor(rule.rarity)} style={{ margin: 0 }}>{rule.rarity}</Tag>
                                  <Text>卡</Text>
                                  {totalPacksLocal > 0 && (
                                    <Text type={rule.conflict ? 'warning' : 'secondary'} style={{ fontSize: 12 }}>
                                      （当前约 {rule.avgPerPack % 1 === 0 ? rule.avgPerPack : rule.avgPerPack.toFixed(2)} 张/包）
                                    </Text>
                                  )}
                                </Space>
                              </Col>
                              <Col>
                                <Popconfirm title="确认删除？" onConfirm={() => handleDeleteRule(rule.id)}>
                                  <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                                </Popconfirm>
                              </Col>
                            </Row>
                          ))}
                        </div>
                      )}
                      <Form form={ruleForm} layout="inline" onFinish={handleUpsertRule} style={{ marginBottom: 12 }}>
                        <Form.Item name="rarity" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                          <Select
                            size="small"
                            placeholder="稀有度"
                            style={{ width: 80 }}
                            showSearch
                            options={uniqueRarities.map(r => ({ value: r, label: r }))}
                          />
                        </Form.Item>
                        <Form.Item name="operator" initialValue="exactly" style={{ marginBottom: 0 }}>
                          <Select size="small" style={{ width: 72 }}>
                            <Select.Option value="exactly">恰好</Select.Option>
                            <Select.Option value="at_least">至少</Select.Option>
                            <Select.Option value="at_most">最多</Select.Option>
                          </Select>
                        </Form.Item>
                        <Form.Item name="count" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                          <InputNumber size="small" min={1} style={{ width: 60 }} placeholder="张数" />
                        </Form.Item>
                        <Form.Item style={{ marginBottom: 0 }}>
                          <Button size="small" type="dashed" htmlType="submit" icon={<PlusOutlined />}>添加规则</Button>
                        </Form.Item>
                      </Form>
                    </>
                  );
                })()}

                {(() => {
                  const uniqueRarities = Array.from(new Set(cards.map(c => c.rarity).filter(Boolean)));
                  if (uniqueRarities.length === 0) return null;
                  const localTotal = cards.reduce((sum, c) => sum + (localQuantities[c.id] ?? 0), 0);
                  return (
                    <>
                      <Divider orientation="left" plain style={{ margin: '0 0 8px' }}>按稀有度批量设置数量</Divider>
                      <Row gutter={[12, 8]} style={{ marginBottom: 12 }} wrap>
                        {uniqueRarities.map(rarity => {
                          const rarityTotal = cards
                            .filter(c => c.rarity === rarity)
                            .reduce((sum, c) => sum + (localQuantities[c.id] ?? 0), 0);
                          const prob = localTotal > 0
                            ? (rarityTotal / localTotal * 100).toFixed(1)
                            : null;
                          return (
                          <Col key={rarity}>
                            <Space size={4}>
                              <Tag color={getRarityColor(rarity)} style={{ margin: 0, minWidth: 36, textAlign: 'center' }}>{rarity}</Tag>
                              <InputNumber
                                min={1}
                                size="small"
                                style={{ width: 70 }}
                                placeholder="数量"
                                value={ratioQuickValues[rarity]}
                                onChange={val => setRatioQuickValues(prev => ({ ...prev, [rarity]: val ?? undefined }))}
                              />
                              <Button
                                size="small"
                                type="dashed"
                                loading={ratioQuickLoading}
                                disabled={!ratioQuickValues[rarity]}
                                onClick={() => handleRarityRatioFill(rarity)}
                              >
                                填入全部 {rarity}
                              </Button>
                              {prob !== null && (
                                <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                                  {rarityTotal}张 / 占{prob}%
                                </Text>
                              )}
                            </Space>
                          </Col>
                          );
                        })}
                      </Row>
                    </>
                  );
                })()}
                <Table rowKey="id" dataSource={cards} columns={ratioColumns} pagination={false} size="small" className="ratio-table" />
              </Card>
            ) : (
              <Alert message="请从左侧选择SKU以配置卡牌比例" type="info" showIcon />
            )}
          </Col>
        </Row>
      )}

      {/* SKU Modal */}
      <Modal title={editingSku ? '编辑SKU' : '新增SKU'} open={skuModalVisible} onCancel={() => setSkuModalVisible(false)} onOk={() => skuForm.submit()} okText="保存">
        <Form form={skuForm} layout="vertical" onFinish={handleSkuSubmit}>
          <Row gutter={12}>
            <Col span={10}>
              <Form.Item name="code" label="SKU编码" rules={[{ required: true, message: '编码必填' }]}>
                <Input placeholder="如: R180" />
              </Form.Item>
            </Col>
            <Col span={14}>
              <Form.Item name="name" label="SKU名称" rules={[{ required: true }]}>
                <Input placeholder="如: 黄金版、白银版" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="packSize" label="每包张数" rules={[{ required: true }]} initialValue={5}>
            <InputNumber min={1} max={100} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="thumbnailUrl" label="缩略图">
            <Input placeholder="https://..." style={{ marginBottom: 8 }} />
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.thumbnailUrl !== cur.thumbnailUrl || prev.code !== cur.code}>
              {({ getFieldValue, setFieldValue }) => {
                const url = getFieldValue('thumbnailUrl');
                return (
                  <Space align="start">
                    {url && <img src={`${url}?x-oss-process=image/resize,w_200`} alt="缩略图" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4, border: '1px solid #d9d9d9' }} />}
                    <Upload
                      accept="image/*"
                      showUploadList={false}
                      beforeUpload={async (file) => {
                        const code = getFieldValue('code');
                        if (!code) { message.warning('请先填写SKU编码'); return false; }
                        setSkuThumbUploading(true);
                        try {
                          const fd = new FormData();
                          fd.append('skuCode', code);
                          fd.append('file', file);
                          const res = await fetch(`${BASE_URL}/skus/upload-thumbnail`, { method: 'POST', body: fd });
                          const json = await res.json();
                          if (json.success) {
                            setFieldValue('thumbnailUrl', json.data.url);
                            message.success('上传成功');
                          } else {
                            message.error(json.message);
                          }
                        } catch (e: any) {
                          message.error(e.message);
                        } finally {
                          setSkuThumbUploading(false);
                        }
                        return false;
                      }}
                    >
                      <Button icon={<UploadOutlined />} loading={skuThumbUploading} size="small">上传图片</Button>
                    </Upload>
                  </Space>
                );
              }}
            </Form.Item>
          </Form.Item>
          <Form.Item name="description" label="描述">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

    </Card>
  );
};

// ==================== Restock Alerts Tab ====================

const RestockAlertsTab: React.FC = () => {
  const [groups, setGroups] = useState<Array<{ set: CardSet; cards: CardWithInventory[] }>>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/card-inventory/alerts`);
      const json = await res.json();
      if (json.success) setGroups(json.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalCritical = groups.reduce((s, g) => s + g.cards.filter(c => c.isCriticalStock).length, 0);
  const totalLow = groups.reduce((s, g) => s + g.cards.filter(c => c.isLowStock && !c.isCriticalStock).length, 0);

  const columns = [
    { title: '编号', dataIndex: 'cardNumber', width: 80 },
    { title: '名称', dataIndex: 'name' },
    { title: '稀有度', dataIndex: 'rarity', width: 90, render: (v: string) => <Tag color={getRarityColor(v)}>{v}</Tag> },
    { title: '库存(张)', dataIndex: 'stock', width: 90, render: (v: number, r: CardWithInventory) => <span className={r.isCriticalStock ? 'stock-critical' : 'stock-warning'} style={{ fontWeight: 600 }}>{v}</span> },
    { title: '月消耗', dataIndex: 'monthlyConsumption', width: 80 },
    { title: '预计余量', dataIndex: 'monthsRemaining', width: 90, render: (v: number | null) => v != null ? `${v.toFixed(1)} 月` : '-' },
    { title: '建议补货', dataIndex: 'restockSuggestion', width: 100, render: (v: number) => <Text type="danger" strong>{v} 张</Text> },
    {
      title: '紧急程度', width: 100,
      render: (_: any, r: CardWithInventory) =>
        r.isCriticalStock ? <Tag color="red">严重不足</Tag> : <Tag color="orange">需补货</Tag>,
    },
  ];

  if (!loading && groups.length === 0) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '48px 0', color: '#52c41a' }}>
          <CheckCircleOutlined style={{ fontSize: 48, marginBottom: 12 }} />
          <div style={{ fontSize: 18, fontWeight: 600 }}>所有卡牌库存充足</div>
          <div style={{ color: '#999', marginTop: 8 }}>暂无需要补货的卡牌</div>
        </div>
      </Card>
    );
  }

  return (
    <Card
      title={
        <Space>
          <span>补货预警</span>
          {totalCritical > 0 && <Badge count={totalCritical} style={{ backgroundColor: '#ff4d4f' }} />}
          {totalLow > 0 && <Badge count={totalLow} style={{ backgroundColor: '#faad14' }} />}
        </Space>
      }
      extra={<Button icon={<ReloadOutlined />} onClick={load}>刷新</Button>}
    >
      {totalCritical > 0 && <Alert type="error" showIcon icon={<WarningOutlined />} className="inventory-alert"
        message={`共 ${totalCritical} 张卡牌库存严重不足，请立即补货！`} />}
      {totalLow > 0 && <Alert type="warning" showIcon className="inventory-alert"
        message={`共 ${totalLow} 张卡牌库存不足1个月，建议尽快安排补货。`} />}

      {groups.map(({ set, cards }) => (
        <Card key={set.id} size="small" title={`${set.code} ${set.name}`}
          style={{ marginBottom: 16 }}
          extra={<Text type="secondary">需补货 {cards.length} 张</Text>}
        >
          <Table
            rowKey="id"
            dataSource={cards}
            columns={columns}
            pagination={false}
            size="small"
            loading={loading}
          />
        </Card>
      ))}
    </Card>
  );
};

// ==================== Main Component ====================

const CardMakerAdmin: React.FC = () => {
  const [sets, setSets] = useState<CardSet[]>([]);
  const [activeTab, setActiveTab] = useState('sets');

  const loadSets = async () => {
    const res = await fetch(`${BASE_URL}/sets`);
    const json = await res.json();
    if (json.success) setSets(json.data);
  };

  useEffect(() => { loadSets(); }, []);

  return (
    <div className="card-maker-admin">
      <Title level={3}>卡牌制作管理</Title>
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="卡牌系列" key="sets">
          <CardSetsTab onSetChange={loadSets} />
        </TabPane>
        <TabPane tab="卡牌管理" key="cards">
          <CardsTab sets={sets} />
        </TabPane>
        <TabPane tab="SKU管理" key="skus">
          <SkuTab sets={sets} />
        </TabPane>
        <TabPane tab="补货预警" key="restock">
          <RestockAlertsTab />
        </TabPane>
      </Tabs>
    </div>
  );
};

export default CardMakerAdmin;
